<template>
  <div>
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">
      {{ $t('QUEUES_MGMT.MY_TICKETS.TITLE') }}
    </h2>
    <p class="text-sm text-slate-600 dark:text-slate-400 mb-3">
      {{ $t('QUEUES_MGMT.MY_TICKETS.SUB_TEXT') }}
    </p>

    <woot-loading-state
      v-if="isLoading"
      :message="$t('QUEUES_MGMT.MY_TICKETS.LOADING')"
    />
    <p v-else-if="!tickets.length" class="text-slate-600 dark:text-slate-400">
      {{ $t('QUEUES_MGMT.MY_TICKETS.EMPTY') }}
    </p>

    <table v-else class="woot-table">
      <thead>
        <th
          v-for="header in $t('QUEUES_MGMT.MY_TICKETS.TABLE_HEADER')"
          :key="header"
        >
          {{ header }}
        </th>
      </thead>
      <tbody>
        <tr v-for="ticket in tickets" :key="ticket.conversation_id">
          <td>
            <router-link :to="conversationUrl(ticket.conversation_id)">
              #{{ ticket.conversation_id }}
            </router-link>
            <p class="text-xs text-slate-500 mb-0">
              {{ ticket.subject || ticket.contact_name || '—' }}
            </p>
          </td>

          <!-- QM §5.4.2 — SLA bar with the absolute time remaining and a warning state -->
          <td>
            <template v-if="ticket.sla_percent !== null">
              <div
                class="h-1.5 w-24 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden"
              >
                <div
                  class="h-full rounded"
                  :class="slaBarClass(ticket)"
                  :style="{ width: `${Math.min(ticket.sla_percent, 100)}%` }"
                />
              </div>
              <span class="text-xs" :class="slaTextClass(ticket)">
                {{ slaLabel(ticket) }}
              </span>
            </template>
            <span v-else class="text-xs text-slate-400">
              {{ $t('QUEUES_MGMT.MY_TICKETS.NO_SLA') }}
            </span>
          </td>

          <!-- QM §5.4.2 — lock indicator: icon AND text, never colour alone (§5.10) -->
          <td>
            <span
              v-if="ticket.locked"
              class="inline-flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200"
            >
              <fluent-icon icon="lock-closed" size="12" />
              {{ $t('QUEUES_MGMT.MY_TICKETS.LOCKED') }}
            </span>
            <span v-else class="text-xs text-slate-400">—</span>
          </td>

          <td class="text-xs">{{ idleLabel(ticket) }}</td>
          <td class="text-xs">{{ ticket.queue_name || '—' }}</td>

          <!-- QM §5.4.2, §5.4.3 — the row actions ride on the `Queues` update permission -->
          <td class="button-wrapper">
            <template v-if="canActOnQueues">
              <woot-button
                v-tooltip.top="$t('QUEUES_MGMT.MY_TICKETS.OPEN')"
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                icon="arrow-right"
                @click="openTicket(ticket)"
              />
              <woot-button
                v-if="ticket.queue_id"
                v-tooltip.top="
                  ticket.locked
                    ? $t('QUEUES_MGMT.CONVERSATION.UNLOCK')
                    : $t('QUEUES_MGMT.CONVERSATION.LOCK')
                "
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                icon="lock-closed"
                :is-loading="busyId === ticket.conversation_id"
                @click="toggleLock(ticket)"
              />
              <woot-button
                v-if="ticket.queue_id"
                v-tooltip.top="$t('QUEUES_MGMT.CONVERSATION.RETURN')"
                variant="smooth"
                size="tiny"
                color-scheme="warning"
                icon="arrow-reply"
                :is-loading="busyId === ticket.conversation_id"
                @click="onReturn(ticket)"
              />
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <woot-confirm-modal
      ref="returnConfirm"
      :title="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.TITLE')"
      :description="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.DESC')"
      :confirm-label="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.YES')"
      :cancel-label="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  formatDistanceToNowStrict,
  parseISO,
  differenceInSeconds,
} from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import QueueActionsAPI from 'dashboard/api/queueActions';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// QM §5.4.2 — "approaching breach" threshold for the SLA warning state.
const SLA_WARNING_PERCENT = 80;
// QM §5.7 — one sweep assigns up to MAX_ASSIGNMENTS_PER_SWEEP (20) tickets, so the
// push events arrive as a burst; coalesce them into a single refetch, well inside 2s.
const RELOAD_DEBOUNCE_MS = 500;

export default {
  mixins: [alertMixin, queueSupervisionMixin],
  data() {
    return {
      tickets: [],
      isLoading: false,
      busyId: null,
      reloadTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
  },
  mounted() {
    this.load();
    // QM §5.7 — this list keeps its own copy of the data (own endpoint, own payload
    // shape), so the conversations store updating on push does not reach it.
    window.bus.$on(BUS_EVENTS.CONVERSATION_UPDATED, this.onConversationUpdated);
  },
  beforeDestroy() {
    window.bus.$off(
      BUS_EVENTS.CONVERSATION_UPDATED,
      this.onConversationUpdated
    );
    clearTimeout(this.reloadTimer);
  },
  methods: {
    // QM §5.7 — refetch when a conversation that is (or just was) ours changes: newly
    // assigned to us, or taken away/locked/returned while sitting in the list.
    onConversationUpdated(data) {
      const isMine = data?.meta?.assignee?.id === this.currentUser?.id;
      const isListed = this.tickets.some(
        ticket => ticket.conversation_id === data?.id
      );
      if (!isMine && !isListed) return;

      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.load(), RELOAD_DEBOUNCE_MS);
    },
    async load() {
      this.isLoading = true;
      try {
        this.tickets = await this.$store.dispatch('queues/getMyTickets');
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.MY_TICKETS.TOAST.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    conversationUrl(conversationId) {
      return frontendURL(
        `accounts/${this.currentAccountId}/conversations/${conversationId}`
      );
    },
    openTicket(ticket) {
      this.$router.push(this.conversationUrl(ticket.conversation_id));
    },
    isAtRisk(ticket) {
      return ticket.sla_percent >= SLA_WARNING_PERCENT;
    },
    slaBarClass(ticket) {
      return this.isAtRisk(ticket) ? 'bg-red-500' : 'bg-woot-500';
    },
    slaTextClass(ticket) {
      return this.isAtRisk(ticket)
        ? 'text-red-600 dark:text-red-400'
        : 'text-slate-500';
    },
    // Absolute time remaining, plus an explicit text state so the bar is never colour-only.
    slaLabel(ticket) {
      if (!ticket.sla_deadline) {
        return `${ticket.sla_percent}%`;
      }
      const deadline = parseISO(ticket.sla_deadline);
      if (differenceInSeconds(deadline, new Date()) <= 0) {
        return this.$t('QUEUES_MGMT.MY_TICKETS.SLA_BREACHED');
      }
      return this.$t('QUEUES_MGMT.MY_TICKETS.SLA_LEFT', {
        time: formatDistanceToNowStrict(deadline),
      });
    },
    idleLabel(ticket) {
      if (!ticket.last_agent_activity_at) {
        return this.$t('QUEUES_MGMT.MY_TICKETS.NEVER_TOUCHED');
      }
      return formatDistanceToNowStrict(
        parseISO(ticket.last_agent_activity_at),
        {
          addSuffix: true,
        }
      );
    },
    async toggleLock(ticket) {
      this.busyId = ticket.conversation_id;
      try {
        if (ticket.locked) {
          await QueueActionsAPI.unlock(ticket.conversation_id);
        } else {
          await QueueActionsAPI.lock(ticket.conversation_id);
        }
        await this.load();
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.MY_TICKETS.TOAST.ERROR')
        );
      } finally {
        this.busyId = null;
      }
    },
    async onReturn(ticket) {
      const confirmed = await this.$refs.returnConfirm.showConfirmation();
      if (confirmed) {
        this.returnToQueue(ticket);
      }
    },
    async returnToQueue(ticket) {
      this.busyId = ticket.conversation_id;
      try {
        await QueueActionsAPI.returnToQueue(ticket.conversation_id);
        this.showAlert(this.$t('QUEUES_MGMT.CONVERSATION.TOAST.RETURNED'));
        await this.load();
        this.$emit('returned', ticket.conversation_id);
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.MY_TICKETS.TOAST.ERROR')
        );
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>
